import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiDownload, FiTrendingDown, FiUsers } from 'react-icons/fi';
import { fetchTrends, exportAnalyticsCsv } from '../../utils/sentimentApi';

function TrendChart({ points }) {
  if (!points || points.length === 0) return null;
  const W = 560, H = 120, padX = 8, padY = 12;
  const scores = points.map(p => p.avg_score);
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = max - min || 0.01;
  const xs = points.map((_, i) => padX + (i / Math.max(points.length - 1, 1)) * (W - padX * 2));
  const ys = points.map(v => H - padY - ((v.avg_score - min) / range) * (H - padY * 2));
  const line = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${line} L${xs[xs.length - 1].toFixed(1)},${H} L${xs[0].toFixed(1)},${H} Z`;
  const isNeg = (min + max) / 2 < 0;
  const color = isNeg ? 'var(--sm-red)' : 'var(--sm-green)';

  return (
    <div className="sm-card" style={{ overflowX: 'auto' }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Avg Sentiment Score Over Time</div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ minWidth: 320 }} aria-label="Sentiment trend">
        <defs>
          <linearGradient id="trend-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity=".22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#trend-grad)" />
        <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={xs[i]} cy={ys[i]} r="4" fill={color} />
            <text x={xs[i]} y={H - 2} textAnchor="middle" fontSize="9" fill="var(--sm-muted)">
              {new Date(p.period).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </text>
            <text x={xs[i]} y={ys[i] - 7} textAnchor="middle" fontSize="9" fill={color} fontWeight="600">
              {p.avg_score.toFixed(2)}
            </text>
          </g>
        ))}
      </svg>

      <div style={{ display: 'flex', gap: 24, marginTop: 12, flexWrap: 'wrap' }}>
        {points.map((p, i) => (
          <div key={i} style={{ textAlign: 'center', minWidth: 60 }}>
            <div style={{ fontSize: 11, color: 'var(--sm-muted)', marginBottom: 2 }}>
              {new Date(p.period).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', fontSize: 11 }}>
              <span style={{ color: 'var(--sm-red)' }}>−{p.negative_count}</span>
              <span style={{ color: 'var(--sm-amber)' }}>{p.neutral_count}</span>
              <span style={{ color: 'var(--sm-green)' }}>+{p.positive_count}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BulkSpikesSection({ spikes }) {
  if (!spikes || spikes.length === 0) {
    return (
      <div className="sm-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiTrendingDown size={15} color="var(--sm-red)" /> Bulk Spikes
        </div>
        <p style={{ fontSize: 13, color: 'var(--sm-muted)', margin: 0 }}>No bulk spikes detected in this period.</p>
      </div>
    );
  }

  return (
    <div className="sm-card" style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <FiTrendingDown size={15} color="var(--sm-red)" /> Bulk Spikes ({spikes.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {spikes.map((s, i) => (
          <div key={i} style={{ background: 'var(--sm-bg)', borderRadius: 8, padding: '12px 16px', borderLeft: '3px solid var(--sm-red)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, textTransform: 'capitalize', marginBottom: 4 }}>
                  {s.common_issue?.replace(/_/g, ' ')}
                </div>
                <div style={{ fontSize: 12, color: 'var(--sm-muted)' }}>
                  {s.affected_customers} customers · last {s.time_period_days}d · avg drop {s.avg_sentiment_drop?.toFixed(2)}
                </div>
              </div>
              {s.total_arr_at_risk != null && (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--sm-muted)' }}>ARR at risk</div>
                  <div style={{ fontWeight: 700, color: 'var(--sm-red)', fontSize: 15 }}>
                    ${s.total_arr_at_risk.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
            {s.suggested_action && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--sm-primary)', background: 'var(--sm-primary-lt)', borderRadius: 6, padding: '5px 10px' }}>
                💡 {s.suggested_action}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DecliningSection({ declining }) {
  if (!declining || declining.length === 0) {
    return (
      <div className="sm-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiUsers size={15} color="var(--sm-amber)" /> Declining Customers
        </div>
        <p style={{ fontSize: 13, color: 'var(--sm-muted)', margin: 0 }}>No declining customers detected.</p>
      </div>
    );
  }

  return (
    <div className="sm-card" style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <FiUsers size={15} color="var(--sm-amber)" /> Declining Customers ({declining.length})
      </div>
      <div className="sm-table-wrap">
        <table className="sm-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Channel</th>
              <th>Current Score</th>
              <th>Drop</th>
            </tr>
          </thead>
          <tbody>
            {declining.map((c, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{c.customer_id || c.customer_name || '—'}</td>
                <td style={{ textTransform: 'capitalize' }}>{c.channel || '—'}</td>
                <td style={{ color: 'var(--sm-red)', fontWeight: 600 }}>{c.sentiment_score?.toFixed(2) ?? '—'}</td>
                <td style={{ color: 'var(--sm-red)', fontSize: 12 }}>{c.score_drop != null ? `▼ ${c.score_drop.toFixed(2)}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SentimentAnalytics() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [exporting, setExporting] = useState(false);
  const [days, setDays]           = useState('30');
  const [granularity, setGranularity] = useState('week');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchTrends({ days, granularity });
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [days, granularity]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAnalyticsCsv(days);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="sm-section-header">
        <h2 className="sm-section-title">Analytics &amp; Trends</h2>
        <div className="sm-filters">
          <select className="sm-select" value={days} onChange={e => setDays(e.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <select className="sm-select" value={granularity} onChange={e => setGranularity(e.target.value)}>
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
          <button className="sm-btn sm-btn-ghost" onClick={load} title="Refresh"><FiRefreshCw size={14} /></button>
          <button
            className="sm-btn sm-btn-outline"
            onClick={handleExport}
            disabled={exporting}
            style={{ gap: 6 }}
          >
            <FiDownload size={14} /> {exporting ? 'Exporting…' : 'CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>
      )}

      {loading ? (
        <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading analytics…</span></div>
      ) : !data ? null : (
        <>
          <TrendChart points={data.trend_points} />
          <BulkSpikesSection spikes={data.bulk_spikes} />
          <DecliningSection declining={data.declining_customers} />
        </>
      )}
    </div>
  );
}
